// src/Qr.js
import React, { useState, useRef } from 'react';
// Corrected import: Use QRCodeCanvas for rendering to canvas
import { QRCodeCanvas } from 'qrcode.react';
import './Qr.css'; // Import the CSS

function QrGenerator() {
  const [upiId, setUpiId] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  const [qrValue, setQrValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const qrCodeWrapperRef = useRef(null);

  const generateUpiLink = () => {
    if (!upiId.trim()) {
      setErrorMessage('UPI ID is required.');
      setQrValue('');
      return null;
    }
    const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
    const mobileVpaRegex = /^\d{10,14}$/;
    if (!upiRegex.test(upiId.trim()) && !mobileVpaRegex.test(upiId.trim())) {
      setErrorMessage('Invalid UPI ID format. Use name@bank or a valid VPA-linked mobile number.');
      setQrValue('');
      return null;
    }

    let link = `upi://pay?pa=${encodeURIComponent(upiId.trim())}`;
    if (payeeName.trim()) {
      link += `&pn=${encodeURIComponent(payeeName.trim())}`;
    }
    if (amount.trim() && !isNaN(parseFloat(amount.trim())) && parseFloat(amount.trim()) > 0) {
      link += `&am=${encodeURIComponent(parseFloat(amount.trim()).toFixed(2))}`;
    } else if (amount.trim() && (isNaN(parseFloat(amount.trim())) || parseFloat(amount.trim()) <= 0)) {
      setErrorMessage('Invalid amount. Please enter a positive number.');
      setQrValue('');
      return null;
    }
    if (notes.trim()) {
      link += `&tn=${encodeURIComponent(notes.trim())}`;
    }
    link += `&cu=INR`;

    setErrorMessage('');
    return link;
  };

  const handleGenerateQr = (e) => {
    e.preventDefault();
    const upiLink = generateUpiLink();
    if (upiLink) {
      setQrValue(upiLink);
    }
  };

  const handleDownloadQr = () => {
    if (!qrValue || !qrCodeWrapperRef.current) {
      setErrorMessage('Please generate a QR code first.');
      return;
    }

    // The QRCodeCanvas component directly renders a <canvas>
    // So qrCodeWrapperRef.current will be the div, and its first child should be the canvas
    const canvas = qrCodeWrapperRef.current.querySelector('canvas');
    if (canvas) {
      try {
        const pngUrl = canvas
          .toDataURL('image/png')
          .replace('image/png', 'image/octet-stream');

        let downloadLink = document.createElement('a');
        const filenameBase = upiId.split('@')[0] || 'upi-qrcode';
        downloadLink.download = `${filenameBase}_upi_qr.png`;
        downloadLink.href = pngUrl;

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        setErrorMessage('');
      } catch (error) {
        console.error("Error generating data URL or downloading:", error);
        setErrorMessage("Could not download QR code. Please try again.");
      }
    } else {
      setErrorMessage('QR Code image element not found. Please regenerate.');
      console.error("Canvas element not found within qrCodeWrapperRef");
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  return (
    <div className="qr-generator-container">
      <h1>Golu-OYO Service ka qr Code Generator</h1>
      <h2>Create and download your UPI QR code instantly</h2>

      {errorMessage && <p className="error-message">{errorMessage}</p>}

      <form onSubmit={handleGenerateQr}>
        <div className="input-group">
          <label htmlFor="upiId">UPI ID (e.g., yourname@bank or 98XXXXXX00@upi)*</label>
          <input
            type="text"
            id="upiId"
            value={upiId}
            onChange={(e) => setUpiId(e.target.value)}
            placeholder="Enter your UPI ID"
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="payeeName">Payee Name (Optional)</label>
          <input
            type="text"
            id="payeeName"
            value={payeeName}
            onChange={(e) => setPayeeName(e.target.value)}
            placeholder="Your Name / Business Name"
          />
        </div>

        <div className="input-group">
          <label htmlFor="amount">Amount (Optional, e.g., 100.00)</label>
          <input
            type="text"
            id="amount"
            value={amount}
            onChange={handleAmountChange}
            placeholder="0.00"
            inputMode="decimal"
          />
        </div>

        <div className="input-group">
          <label htmlFor="notes">Transaction Notes (Optional)</label>
          <input
            type="text"
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Payment for services, goods, etc."
          />
        </div>

        <button type="submit" className="btn" disabled={!upiId.trim()}>
          Generate QR Code
        </button>
      </form>

      {qrValue && (
        <div className="qr-code-display-section">
          <p>Scan this QR code with any UPI-enabled app:</p>
          {/*
            The ref is on the wrapper div. QRCodeCanvas will render a canvas inside it.
            We don't need renderAs="canvas" prop for QRCodeCanvas as it's implied.
          */}
          <div ref={qrCodeWrapperRef} className="qr-code-image-wrapper">
            <QRCodeCanvas // Use the correctly imported component
              value={qrValue}
              size={220}
              level={"H"}
              includeMargin={true}
              bgColor="#FFFFFF"
              fgColor="#000000"
              // renderAs="canvas" is not needed for QRCodeCanvas, it's the default
            />
          </div>
          <button onClick={handleDownloadQr} className="btn download-btn">
            Download QR Code (PNG)
          </button>
        </div>
      )}
    </div>
  );
}

export default QrGenerator;